import React from 'react';
import { track } from '../lib/analytics';
import { apiGet, apiPost, ApiRequestError } from '../lib/apiClient';
import { LIVE_API, ENDPOINTS } from '../lib/config';
export type PortalRole = 'client-user' | 'client-admin' | 'cavalla-team' | 'super-admin';
export interface PortalUser {
  id: string;
  name: string;
  email: string;
  role: PortalRole;
  organisationId: string;
  organisationName: string;
  mfaEnabled: boolean;
}
interface AuthOutcome {
  ok: boolean;
  error?: string;
  mfaRequired?: boolean;
}
interface PortalAuthValue {
  user: PortalUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthOutcome>;
  completeMfa: (code: string) => Promise<AuthOutcome>;
  sendMagicLink: (email: string) => Promise<AuthOutcome>;
  signOut: () => void;
  pendingMfa: boolean;
}
const DEMO_USERS: Record<string, {
  password: string;
  user: PortalUser;
}> = {
  'a.mensah@northbridge.org': {
    password: 'cavalla2026',
    user: {
      id: 'u-1',
      name: 'Ama Mensah',
      email: 'a.mensah@northbridge.org',
      role: 'client-admin',
      organisationId: 'org-northbridge',
      organisationName: 'Northbridge Development Trust',
      mfaEnabled: true
    }
  }
};
function messageFor(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}
const PortalAuthContext = React.createContext<PortalAuthValue | null>(null);
export function PortalAuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = React.useState<PortalUser | null>(null);
  const [loading, setLoading] = React.useState(LIVE_API);
  const [pendingMfa, setPendingMfa] = React.useState(false);
  const pendingUser = React.useRef<PortalUser | null>(null);

  /**
   * GET /api/auth/session
   * Restores the signed in user from the HttpOnly session cookie on first load,
   * so a refresh inside the portal does not sign the client out.
   */
  React.useEffect(() => {
    if (!LIVE_API) return;
    let active = true;
    apiGet<{
      user: PortalUser | null;
    }>(ENDPOINTS.session).then(result => {
      if (active) setUser(result?.user ?? null);
    }).catch(() => {
      if (active) setUser(null);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  /**
   * POST /api/auth/login
   * Rate limited server side. On success the route sets a short lived access
   * cookie and a rotating refresh cookie, or reports that MFA is required.
   */
  const signIn = React.useCallback(async (email: string, password: string): Promise<AuthOutcome> => {
    if (LIVE_API) {
      try {
        const result = await apiPost<{
          user: PortalUser | null;
          mfaRequired?: boolean;
        }>(ENDPOINTS.login, {
          email: email.trim().toLowerCase(),
          password
        });
        if (result?.mfaRequired) {
          setPendingMfa(true);
          return {
            ok: true,
            mfaRequired: true
          };
        }
        if (result?.user) {
          setUser(result.user);
          track('portal_login', {
            role: result.user.role
          });
          return {
            ok: true
          };
        }
        return {
          ok: false,
          error: 'We could not sign you in. Please try again.'
        };
      } catch (error) {
        return {
          ok: false,
          error: messageFor(error, 'Those details do not match an account. Please try again.')
        };
      }
    }
    await new Promise(resolve => setTimeout(resolve, 600));
    const record = DEMO_USERS[email.trim().toLowerCase()];
    if (!record || record.password !== password) {
      return {
        ok: false,
        error: 'Those details do not match an account. Please try again.'
      };
    }
    if (record.user.mfaEnabled) {
      pendingUser.current = record.user;
      setPendingMfa(true);
      return {
        ok: true,
        mfaRequired: true
      };
    }
    setUser(record.user);
    track('portal_login', {
      role: record.user.role
    });
    return {
      ok: true
    };
  }, []);

  /** POST /api/auth/mfa */
  const completeMfa = React.useCallback(async (code: string): Promise<AuthOutcome> => {
    if (LIVE_API) {
      try {
        const result = await apiPost<{
          user: PortalUser;
        }>(ENDPOINTS.mfa, {
          code: code.trim()
        });
        setUser(result.user);
        setPendingMfa(false);
        track('portal_login', {
          role: result.user.role
        });
        return {
          ok: true
        };
      } catch (error) {
        return {
          ok: false,
          error: messageFor(error, 'That code is not valid. Codes expire after thirty seconds.')
        };
      }
    }
    await new Promise(resolve => setTimeout(resolve, 450));
    if (code.trim() !== '204815') {
      return {
        ok: false,
        error: 'That code is not valid. Codes expire after thirty seconds.'
      };
    }
    const next = pendingUser.current;
    if (!next) return {
      ok: false,
      error: 'Your session expired. Please sign in again.'
    };
    setUser(next);
    setPendingMfa(false);
    pendingUser.current = null;
    track('portal_login', {
      role: next.role
    });
    return {
      ok: true
    };
  }, []);

  /**
   * POST /api/auth/magic-link
   * Always reports success, so the response cannot be used to discover which
   * addresses hold an account.
   */
  const sendMagicLink = React.useCallback(async (email: string): Promise<AuthOutcome> => {
    if (LIVE_API) {
      try {
        await apiPost(ENDPOINTS.magicLink, {
          email: email.trim().toLowerCase()
        });
      } catch {
        /* deliberately silent */
      }
      return {
        ok: true
      };
    }
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      ok: true
    };
  }, []);

  /** POST /api/auth/logout, which clears both cookies server side. */
  const signOut = React.useCallback(() => {
    if (LIVE_API) {
      void apiPost(ENDPOINTS.logout, {}).catch(() => {
        /* the local state is cleared regardless */
      });
    }
    setUser(null);
    setPendingMfa(false);
    pendingUser.current = null;
  }, []);
  const value = React.useMemo(() => ({
    user,
    loading,
    signIn,
    completeMfa,
    sendMagicLink,
    signOut,
    pendingMfa
  }), [user, loading, signIn, completeMfa, sendMagicLink, signOut, pendingMfa]);
  return <PortalAuthContext.Provider value={value} data-id="element-403" data-mp="2kgvw0sfe9xhx">{children}</PortalAuthContext.Provider>;
}
export function usePortalAuth(): PortalAuthValue {
  const context = React.useContext(PortalAuthContext);
  if (!context) throw new Error('usePortalAuth must be used inside PortalAuthProvider');
  return context;
}